import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTradingOrderCancelOrder, useTradingOrderListOrders } from '@/lib/endpoints';
import { invalidateTradingCaches } from '@/lib/trading/invalidate-trading-caches';
import { TradingOrderListOrdersStatus } from '@/lib/models';
import type { MarketOptionDto, PlaeeOrderResponseDto } from '@/lib/models';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import { ORDER_FAILURE_MESSAGE, toOrderFailureReason } from '@/lib/trading/order-failure';
import { restingCashDollars } from '@/lib/trading/resting-cash';
import { fmtUsd } from '@/lib/trading/format';
import { cn } from '@/lib/utils';
import { CollapsibleSection } from './CollapsibleSection';
import { LimitOrderPopup } from './LimitOrderPopup';
import type { BuyMarketContext } from './BuyInPopup';

export interface MarketOpenOrdersProps {
  /** The event's tradeable options — orders are scoped to their outcomes. */
  options: MarketOptionDto[];
  /** The event name, used as the Modify sheet's header context. */
  eventName: string;
  /** Section title (admin-ordered). */
  title: string;
  /** Initial expanded state. */
  defaultOpen?: boolean;
  /** Expand/collapse analytics. */
  onToggle?: (open: boolean) => void;
}

/**
 * Open Orders section for the binary market page (sell/limit PRD §6, Figma
 * node 6557-173752) — the user's resting limit orders in this event.
 *
 * Header shows the order count + a "Resting $X" chip (cash tied up in resting
 * BUY orders: Σ unfilled contracts × limit price — sell rows tie up no cash).
 * Each row: answer + Buy/Sell pill · limit price · filled/total contracts ·
 * **Modify** (reopens {@link LimitOrderPopup} in amend mode — re-queues, loses
 * time priority) · **Cancel** (already-filled portion stays a position). Fill
 * and cancel events refresh the list via the page-level realtime sync.
 */
export function MarketOpenOrders({ options, eventName, title, defaultOpen, onToggle }: MarketOpenOrdersProps) {
  const queryClient = useQueryClient();
  const tradingEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_ORDER_PLACEMENT);
  const amendEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_AMEND);
  const [modifying, setModifying] = useState<PlaeeOrderResponseDto | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // External-outcome id → option, for scoping the account-wide order list to
  // this event and labelling each row.
  const optionByOutcome = new Map<string, MarketOptionDto>();
  for (const o of options) {
    optionByOutcome.set(o.yes.externalOutcomeId, o);
    optionByOutcome.set(o.no.externalOutcomeId, o);
  }

  const { data: orders = [] } = useTradingOrderListOrders({ status: TradingOrderListOrdersStatus.open });
  const rows = orders.filter((o) => optionByOutcome.has(o.outcomeId) && o.leavesQty > 0);

  const restingDollars = restingCashDollars(rows);

  const cancelMutation = useTradingOrderCancelOrder({
    mutation: {
      onSuccess: (result) => {
        if (!result.success) {
          setError(ORDER_FAILURE_MESSAGE[toOrderFailureReason(result.errorCode)]);
          return;
        }
        invalidateTradingCaches(queryClient);
      },
      onError: () => setError(ORDER_FAILURE_MESSAGE.network),
      onSettled: () => setCancellingId(null),
    },
  });

  function handleCancel(order: PlaeeOrderResponseDto) {
    if (!tradingEnabled || !order.clientOrderId || cancellingId) return;
    setError(null);
    setCancellingId(order.clientOrderId);
    cancelMutation.mutate({
      orderId: order.orderId || order.clientOrderId,
      data: {
        origClientOrderId: order.clientOrderId,
        outcomeId: order.outcomeId,
        side: order.contractSide,
        action: order.action,
        count: order.leavesQty,
      },
    });
  }

  // The Modify sheet needs the order's option as its market context.
  const modifyingOption = modifying ? optionByOutcome.get(modifying.outcomeId) : undefined;
  const modifyContext: BuyMarketContext | null = modifying && modifyingOption ? { ...modifyingOption, eventName } : null;

  return (
    <CollapsibleSection
      title={`${rows.length} ${title}`}
      defaultOpen={defaultOpen}
      onToggle={onToggle}
      disabled={rows.length === 0}
      summary={
        rows.length > 0 ? (
          <span className="rounded-pill bg-warning/10 px-2.5 py-0.5 text-sm font-medium text-warning">
            Resting {fmtUsd(restingDollars)}
          </span>
        ) : (
          <span className="text-muted">No open orders</span>
        )
      }
    >
      <div className="flex flex-col gap-2">
        {/* Column headers — Answer · Limit · Contract (figma 6557-173752). */}
        <div className="flex items-center pl-3.5 pr-1 text-[13px] text-muted/70">
          <span className="flex-1">Answer</span>
          <span className="w-12 shrink-0 text-center">Limit</span>
          <span className="w-14 shrink-0 text-center">Contract</span>
          <span className="ml-2 w-[132px] shrink-0" />
        </div>
        <div className="-mt-1 h-px bg-border" />

        {rows.map((order) => {
          const option = optionByOutcome.get(order.outcomeId);
          const label = option?.label ?? (order.contractSide === 'yes' ? 'Yes' : 'No');
          const isBuy = order.action === 'buy';
          const key = order.clientOrderId ?? order.orderId;
          return (
            <div key={key} className="flex h-9 items-center rounded-pill bg-surface-muted pl-3.5 pr-1">
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="truncate text-[13px] font-semibold text-accent-dark">{label}</span>
                <span
                  className={cn(
                    'shrink-0 rounded-pill px-2 py-px text-[11px] font-semibold',
                    isBuy ? 'bg-success/10 text-success-dark' : 'bg-danger/10 text-danger-alt',
                  )}
                >
                  {isBuy ? 'Buy' : 'Sell'}
                </span>
              </span>
              <span className="w-12 shrink-0 text-center text-[13px] font-semibold text-primary tabular-nums">
                {order.price}¢
              </span>
              <span className="w-14 shrink-0 text-center text-[13px] tabular-nums">
                <span className="text-primary">{order.cumQty}</span>
                <span className="text-muted">/{order.orderQty}</span>
              </span>
              <span className="ml-2 flex w-[132px] shrink-0 items-center justify-end gap-1.5">
                {/* Only buy orders are modifiable here — no sell-order entry in scope. */}
                {isBuy && (
                  <button
                    type="button"
                    onClick={() => setModifying(order)}
                    disabled={!tradingEnabled || !amendEnabled || !order.clientOrderId}
                    className="h-7 rounded-pill bg-gradient-to-b from-accent to-accent-dark px-3 text-[13px] font-semibold text-white shadow-inset-glow disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Modify
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleCancel(order)}
                  disabled={!tradingEnabled || !order.clientOrderId || cancellingId === order.clientOrderId}
                  className="h-7 rounded-pill border-[1.5px] border-danger-alt px-3 text-[13px] font-semibold text-danger-alt disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {cancellingId === order.clientOrderId ? 'Cancelling…' : 'Cancel'}
                </button>
              </span>
            </div>
          );
        })}
        {error && <p className="text-center text-[13px] font-medium text-danger-alt">{error}</p>}
      </div>

      <LimitOrderPopup market={modifyContext} amendOrder={modifying} onClose={() => setModifying(null)} />
    </CollapsibleSection>
  );
}
